import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TreeItem from '@mui/lab/TreeItem';
import TreeView from '@mui/lab/TreeView';
import Box from '@mui/material/Box';
import ButtonGroup from '@mui/material/ButtonGroup';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ReactNode, useEffect, useState } from 'react';
import { DeleteButton, EditButton, ListBase, ListProps, ListToolbar, Loading, RecordContextProvider, useGetList, useListContext, useResourceContext } from 'react-admin';

export type TreeNode<T extends {} = {}> = {
  id: number;
  tree_node_parent_id: number | null;
  tree_node_parent_order: number | null;
  //
  _isRoot: boolean;
  _children: Array<TreeNode<T>>;
  _parentIds: Array<number>;
} & Partial<T>

interface NestedTreeItemLabelProps {
  listItem: (node: TreeNode) => ReactNode,
  listItemButtons?: (node: TreeNode) => ReactNode,
  node: TreeNode,
  recordsById: { [id: number]: TreeNode },
  showActionButtons: boolean,
}

const NestedTreeItemLabel = ({ listItem, listItemButtons, node, recordsById, showActionButtons }: NestedTreeItemLabelProps) => {
  // console.log('NestedTreeItemLabel', listItemButtons);
  return (
    <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(224,224,224,1)', marginRight: `18px` }}>
      {listItem(recordsById[node.id])}
      {showActionButtons && !recordsById[node.id]._isRoot
        ? <>
          <span style={{ flex: 1 }}></span>
          <ButtonGroup variant="text" size="medium">
            {listItemButtons
              ? listItemButtons(recordsById[node.id])
              : <>
                <EditButton />
                <DeleteButton />
              </>
            }
          </ButtonGroup>
        </>
        : null}
    </Box>)
}

interface NestedTreeItemProps {
  listItem: (node: TreeNode) => ReactNode,
  listItemButtons?: (node: TreeNode) => ReactNode,
  node: TreeNode,
  recordsById: { [id: number]: TreeNode },
  showActionButtons: boolean,
}

const NestedTreeItem = ({ listItem, listItemButtons, node, recordsById, showActionButtons }: NestedTreeItemProps) => {
  return (
    <RecordContextProvider value={node}>
      <TreeItem
        nodeId={`${node.id}`}
        label={<NestedTreeItemLabel listItem={listItem} listItemButtons={listItemButtons} recordsById={recordsById} node={node} showActionButtons={showActionButtons} />}
      >
        {undefined === recordsById[node.id]._children || recordsById[node.id]._children.length <= 0
          ? null
          : recordsById[node.id]._children.map((child: TreeNode) =>
            <NestedTreeItem key={child.id} node={child} recordsById={recordsById} listItem={listItem} listItemButtons={listItemButtons} showActionButtons={showActionButtons}></NestedTreeItem>
          )
        }
      </TreeItem>
    </RecordContextProvider>
  )
}

interface TreeListProps extends ListProps {
  listItem: (node: TreeNode) => ReactNode,
  listItemButtons?: (node: TreeNode) => ReactNode,
}

const TreeList = ({ ...props }: TreeListProps) => {
  return (
    <>
      <ListBase {...props}>
        <ListToolbar filters={props.filters}>
        </ListToolbar>
        <Paper>
          <ListChild {...props} />
        </Paper>
      </ListBase>
    </>
  )
}

const RecursiveFillParentIds = (data: { [id: number]: TreeNode }, id: number = 0, parentIds: Array<number> = []) => {
  data[id]._parentIds = parentIds;
  if (data[id] && Array.isArray(data[id]._children) && data[id]._children.length > 0) {
    data[id]._children.forEach((child: TreeNode) => {
      RecursiveFillParentIds(data, child.id, [...parentIds, id])
    })
  }
  return data;
}

interface ListTreeViewProps {
  listItem: (node: TreeNode) => ReactNode,
  listItemButtons?: (node: TreeNode) => ReactNode,
  data: Array<TreeNode>,
  showActionButtons?: boolean,
}
export const ListTreeView = ({ data, listItem, listItemButtons, showActionButtons }: ListTreeViewProps) => {
  const [loaded, setLoaded] = useState(false);
  const rootId = 0;
  const [recordsById, setRecordsById] = useState<{ [id: number]: TreeNode }>({});
  // console.log('ListTreeView', listItemButtons);
  useEffect(() => {
    if (!loaded && data) {
      // console.log(data);
      let byId: { [id: number]: TreeNode } = Object.fromEntries(data.map(row => [row.id, { ...row, _children: [], _parentIds: [], _isRoot: false }]));
      byId[rootId] = { _children: [], _parentIds: [], id: rootId, tree_node_parent_id: null, tree_node_parent_order: 0, _isRoot: true };
      data.forEach((row: TreeNode) => {
        if (null !== row.tree_node_parent_id) {
          byId[row.tree_node_parent_id]._children.push(row);
        } else {
          byId[rootId]._children.push(row);
        }
      });

      RecursiveFillParentIds(byId);

      // console.log(byId);
      setRecordsById(byId);
      setLoaded(true);
    }
  }, [loaded, data]);

  if (!loaded) { return <p>Building Tree</p>; }
  // console.log('ListTreeView', recordsById);
  return (
    <TreeView
      aria-label="Tree"
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpandIcon={<ChevronRightIcon />}
      expanded={Object.keys(recordsById)}
    >
      <NestedTreeItem node={recordsById[rootId]} recordsById={recordsById} listItem={listItem} listItemButtons={listItemButtons} showActionButtons={showActionButtons ?? true}></NestedTreeItem>
    </TreeView>
  )
}

interface ListTreeProps {
  listItem: (node: TreeNode) => ReactNode,
  listItemButtons?: (node: TreeNode) => ReactNode,
  resource?: string,
  showActionButtons?: boolean,
}

export const ListTree = (props: ListTreeProps) => {
  let resource = useResourceContext();
  const { data, isLoading, error } = useGetList<TreeNode>(resource, { pagination: { page: 1, perPage: 1000 }, sort: { field: 'tree_node_parent_order', order: 'ASC' } });
  // const [loaded, setLoaded] = useState(false);
  // const [recordsById, setRecordsById] = useState<{ [id: number]: TreeNode }>({});
  // console.log(listContext);
  // const listContext = useList({ data, isLoading });

  if (isLoading) { return <Loading />; }
  if (error) { return <p>ERROR</p>; }
  // if (!loaded) { return <p>Building Tree</p>; }
  if (data) {
    return (<ListTreeView {...props} data={data}></ListTreeView>)
  } else {
    return null;
  }
}

const ListChild = ({ listItem, listItemButtons, ...props }: TreeListProps): any => {
  const lc = useListContext();
  console.log('ListChild', listItemButtons);
  if (Object.keys(lc.filterValues).length === 0) {
    return <ListTree listItem={listItem} listItemButtons={listItemButtons}></ListTree>
  } else {
    return props.children;
  }
}

const DefaultListItem = (node: TreeNode) => <Typography variant="body2" component="span">{node.id ? node.id : "-- Root --"}</Typography>

ListTree.defaultProps = {
  listItem: DefaultListItem
}

TreeList.defaultProps = {
  listItem: DefaultListItem
}

export default TreeList;
